/**
 * Owner: Masa Bayu
 * File: catalog-serialization.ts
 * Date:Time: 2026-09-15 20:24
 * Description: T1 catalog-safe serialization — marketplace payload ≠ QXP truth.
 */
import { assertNoForbiddenFoundationKey, QXREAD_FOUNDATION_FORBIDDEN } from './authority';
import { assertValidId } from './guards';
import { isMarketplaceReady } from './publication-lifecycle';
import { createQxreadPublicationMetadata, serializeCatalogMetadata, } from './publication-metadata';
import { isVersionLaunchable } from './version-binding';
/** Keys that must never appear in catalog wire payloads. */
export const QXREAD_CATALOG_SERIALIZATION_FORBIDDEN = [
    ...QXREAD_FOUNDATION_FORBIDDEN,
    'qxpBytes',
    'qxpPayload',
    'contentUrl',
    'downloadUrl',
    'flipEngine',
    'readerRuntime',
    'entitlementGrant',
    'checkoutSession',
    'paymentToken',
];
export function createQxreadCatalogEntry(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead catalog entry');
    if (options.ref.publicationId !== options.lifecycle.publicationId) {
        throw new Error('QXRead catalog entry ref/lifecycle publicationId mismatch');
    }
    const metadata = options.metadata ??
        createQxreadPublicationMetadata({
            publicationId: options.ref.publicationId,
            title: options.ref.title,
        });
    if (metadata.publicationId !== options.ref.publicationId) {
        throw new Error('QXRead catalog entry metadata publicationId mismatch');
    }
    if (options.version !== undefined && options.version !== null) {
        if (options.version.publicationId !== options.ref.publicationId) {
            throw new Error('QXRead catalog entry version publicationId mismatch');
        }
    }
    if (options.preview !== undefined && options.preview !== null) {
        assertValidId(options.preview.publicationId, 'QXRead preview publicationId');
        if (options.preview.publicationId !== options.ref.publicationId) {
            throw new Error('QXRead catalog entry preview publicationId mismatch');
        }
    }
    return Object.freeze({
        ref: options.ref,
        lifecycle: options.lifecycle,
        metadata,
        version: options.version ?? null,
        preview: options.preview ?? null,
    });
}
function assertNoForbiddenSerializedKey(record, label) {
    for (const key of Object.keys(record)) {
        if (QXREAD_CATALOG_SERIALIZATION_FORBIDDEN.includes(key)) {
            throw new Error(`${label} must not include forbidden field "${key}"`);
        }
    }
}
/**
 * Produce a marketplace-safe catalog payload.
 * Never includes QXP bytes, content URLs, reader runtime, or entitlement grants.
 */
export function serializeCatalogEntry(entry) {
    const payload = Object.freeze({
        publicationId: entry.ref.publicationId,
        qxpPublicationRef: entry.ref.qxpPublicationRef,
        title: entry.ref.title,
        visibility: entry.ref.visibility,
        refState: entry.ref.state,
        listedVersion: entry.ref.listedVersion,
        listingState: entry.lifecycle.listingState,
        availability: entry.lifecycle.availability,
        marketplaceReady: isMarketplaceReady({
            ref: entry.ref,
            lifecycle: entry.lifecycle,
        }),
        metadata: serializeCatalogMetadata(entry.metadata),
        version: entry.version === null
            ? null
            : Object.freeze({
                bindingId: entry.version.bindingId,
                qxpVersionRef: entry.version.qxpVersionRef,
                versionLabel: entry.version.versionLabel,
                state: entry.version.state,
                integrityHash: entry.version.integrityHash,
                launchable: isVersionLaunchable(entry.version),
            }),
        preview: entry.preview === null
            ? null
            : Object.freeze({
                scope: entry.preview.scope,
                enabled: entry.preview.enabled,
            }),
        authority: Object.freeze({
            publicationTruth: 'qxp',
            catalogRole: 'reference',
            contentAccess: 'not_granted_by_catalog',
        }),
    });
    assertCatalogSerializationSafe(payload);
    return payload;
}
/** Reject payloads that smuggle forbidden authority/content fields. */
export function assertCatalogSerializationSafe(payload) {
    assertNoForbiddenSerializedKey(payload, 'QXRead catalog serialization');
    for (const value of Object.values(payload)) {
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            assertNoForbiddenSerializedKey(value, 'QXRead catalog serialization nested');
        }
    }
}
//# sourceMappingURL=catalog-serialization.js.map